import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
} from "react";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardCvcElementOptions,
  StripeCardExpiryElementChangeEvent,
  StripeCardExpiryElementOptions,
  StripeCardNumberElement,
  StripeCardNumberElementChangeEvent,
  StripeCardNumberElementOptions,
} from "@stripe/stripe-js";

import { ApiResponseError } from "@/apis/errors";
import Image from "next/image";
import Label from "@/components/atoms/inputForms/Label";
import { TextField } from "@/components/atoms/inputForms/TextField";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";

interface FieldCardError {
  number: string[];
  expiry: string[];
  cvc: string[];
}
const commonOptions: StripeCardNumberElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#000000",
      fontFamily: "Noto Sans JP, monospace",
      "::placeholder": {
        color: "#BCBCBC",
        fontFamily: "Noto Sans JP, monospace",
        lineHeight: "24px",
      },
      lineHeight: "24px",
      fontWeight: 400,
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

const cardNumberOptions: StripeCardNumberElementOptions = {
  ...commonOptions,
  placeholder: "1234 1234 1234 1234",
};

const cardExpiryOptions: StripeCardExpiryElementOptions = {
  ...commonOptions,
  placeholder: "01/25",
};

const cardCvcOptions: StripeCardCvcElementOptions = {
  ...cardNumberOptions,
  placeholder: "123",
};
export interface IPaymentMethod {
  card: StripeCardNumberElement;
  billing_details: {
    name: string;
  };
}
interface Props {
  onUpdatePaymentMethod: (value: IPaymentMethod) => void | Promise<void>;
  showErrorPart: Dispatch<SetStateAction<boolean>>;
  label: string;
}
export default function FormCreditCard(props: Props) {
  const { onUpdatePaymentMethod, showErrorPart, label } = props;

  const stripe = useStripe();
  const elements = useElements();
  const { handlePageError } = useApiResponseErrorHandler();

  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [name, setName] = useState<string>("");

  const [fieldCardErrors, setFieldCardErrors] = useState<FieldCardError>({
    number: [],
    expiry: [],
    cvc: [],
  });

  const handleNumberChange = (data: StripeCardNumberElementChangeEvent) => {
    if (!data.complete && data.error && data.error.code) {
      const errorMessage: string[] = [];
      if (data.error.code === "incomplete_number") {
        errorMessage.push("カード番号は14桁から16桁で入力してください");
      }
      if (data.error.code === "invalid_number") {
        errorMessage.push("カード番号が無効です。");
      }
      if (errorMessage.length === 0) {
        errorMessage.push(data.error.message);
      }
      setFieldCardErrors((prev: FieldCardError) => ({
        ...prev,
        number: errorMessage,
      }));
    }
    if (data.complete) {
      setFieldCardErrors((prev: FieldCardError) => ({ ...prev, number: [] }));
    }
  };

  const handleNameChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    const value = ev.target.value;
    setName(value);

    if (value === "") {
      setFieldErrors(["半角英大文字で入力してください"]);
      return;
    }

    const nameString = value.replace(/\s/g, "");
    if (nameString === "" || !nameString.match(/^[A-Z]*$/)) {
      setFieldErrors(["半角英大文字で入力してください"]);
    } else {
      setFieldErrors([]);
    }
  };

  const handleExpiryChange = (data: StripeCardExpiryElementChangeEvent) => {
    if (!data.complete && data.error && data.error.code) {
      const errorMessage: string[] = [];
      if (data.error.code === "invalid_expiry_year_past") {
        errorMessage.push("カードの有効期限が過ぎています。");
      }
      if (data.error.code === "invalid_expiry_year") {
        errorMessage.push("カードの有効期限の日付が無効です。");
      }
      if (errorMessage.length === 0) {
        errorMessage.push(data.error.message);
      }
      setFieldCardErrors((prev) => ({ ...prev, expiry: errorMessage }));
    }
    if (data.complete) {
      setFieldCardErrors((prev) => ({ ...prev, expiry: [] }));
    }
  };

  const handleCvcChange = (data: StripeCardCvcElementChangeEvent) => {
    if (!data.complete && data.error && data.error.code) {
      const errorMessage: string[] = [];
      if (data.error.code === "incomplete_cvc") {
        errorMessage.push("セキュリティコードは3桁から4桁で入力してください。");
      }
      if (errorMessage.length === 0) {
        errorMessage.push(data.error.message);
      }
      setFieldCardErrors((prev) => ({ ...prev, cvc: errorMessage }));
    }
    if (data.complete) {
      setFieldCardErrors((prev) => ({ ...prev, cvc: [] }));
    }
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        showErrorPart(false);
        if (!name || name.trim() === "") {
          setFieldErrors(["半角英大文字で入力してください"]);
          e.preventDefault();
          showErrorPart(true);
          return;
        }
        if (!stripe || !elements) {
          showErrorPart(true);
          return;
        }
        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
          showErrorPart(true);
          return;
        }
        const { error } = await stripe.createToken(cardNumberElement);
        if (error) {
          showErrorPart(true);
          return;
        }
        void onUpdatePaymentMethod({
          card: cardNumberElement,
          billing_details: {
            name,
          },
        });
      } catch (error) {
        if (error instanceof ApiResponseError) {
          await handlePageError(error);
        }
        throw error;
      }
    },
    [onUpdatePaymentMethod, name, stripe, elements],
  );
  return (
    <form id="register-credit-card-form" onSubmit={(e) => void handleSubmit(e)}>
      <div className="flex items-center gap-2 mb-2 font-bold text-color-gray-600">
        <span className="text-base">{label}</span>
        <div className="inline-block px-2 py-[2px] text-xs leading-[18px] bg-color-red_error text-white rounded">
          必須
        </div>
      </div>
      <div className="py-3 px-4 bg-[#F7F7F7]">
        <Image src={"/user/img/cardbrand.svg"} width={160} height={32} alt="" />
        <div className="my-4">
          <Label label="カード番号" />
          <CardNumberElement
            options={cardNumberOptions}
            onChange={handleNumberChange}
          />
          <ul>
            {fieldCardErrors.number.map((message: string, i: number) => (
              <li key={i} className="text-xs text-error-msg">
                {message}
              </li>
            ))}
          </ul>
        </div>
        <div className="my-4">
          <Label label="カード名義人" />
          <TextField
            inputClassName="h-11"
            name="name"
            value={name}
            placeholder="WATANABE TAROU"
            onChange={handleNameChange}
          />
          <ul>
            {fieldErrors.map((message, i) => (
              <li key={i} className="text-xs text-error-msg">
                {message}
              </li>
            ))}
          </ul>
        </div>
        <div className="my-4">
          <Label label="有効期限（MM/YY）" />
          <CardExpiryElement
            options={cardExpiryOptions}
            onChange={handleExpiryChange}
          />
          <ul>
            {fieldCardErrors.expiry.map((message, i) => (
              <li key={i} className="text-xs text-error-msg">
                {message}
              </li>
            ))}
          </ul>
        </div>
        <div className="my-4">
          <Label label="セキュリティコード" />
          <CardCvcElement options={cardCvcOptions} onChange={handleCvcChange} />
          <ul>
            {fieldCardErrors.cvc.map((message, i) => (
              <li key={i} className="text-xs text-error-msg">
                {message}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mt-2 text-sm text-color-gray_600">
        カード情報を提供すると、yoyacoo
        がその規約に従って、今後の支払いをお客様のカードに請求することを許可することになります。
      </p>
    </form>
  );
}
